import { styled } from '@linaria/react';
import { ScrollArea, Stack } from '@mantine/core';
import { useMemo, useState } from 'react';
import type { StrategicEpic } from '../../../api/tasks-client/task-client.type';
import { useTranslation } from 'react-i18next';
import { ContentPlaceholder } from '../../../components/content-placeholder/content-placeholder';
import { Table } from '../../../components/table/table';
import { useStrategyStore } from '../../../store/strategy-store/strategy-store';
import { TableGroupBy } from '../../../store/strategy-store/strategy-store.type';
import { inkBase, skyLight, skyLighter } from '../../../styles/design-tokens';
import { Icon } from '../../../ui-library/icon/icon';
import { Text } from '../../../ui-library/typography/typography';
import { sortStrategyEntities } from '../components/strategy-entity-table.helpers';
import { getEpicsTableColumns } from './epics-main-section.helpers';

type GroupedEpics = Record<string, StrategicEpic[]>;

interface EpicsMainSectionGroupedProps {
  groupBy: TableGroupBy;
  epics: StrategicEpic[];
}

export const EpicsMainSectionGrouped = ({ groupBy, epics }: EpicsMainSectionGroupedProps) => {
  const i18n = useTranslation();
  const epicsTableColumns = useMemo(() => getEpicsTableColumns(i18n.t), [i18n.t]);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const tableSortBy = useStrategyStore((state) => state.tableSortBy);
  const tableSortDirection = useStrategyStore((state) => state.tableSortDirection);

  // Group epics based on groupBy prop
  const { groupedEpics, orderedGroupKeys } = useMemo(() => {
    const groups: GroupedEpics = {};

    if (groupBy === TableGroupBy.Team) {
      // Group by team - many-to-many (epics appear in all teams they contribute to)
      const teamNameToEpics = new Map<string, StrategicEpic[]>();
      const unmappedEpics: StrategicEpic[] = [];

      for (const epic of epics) {
        const teamNames = epic.contributing_projects.map((p: { name: string }) => p.name);

        if (teamNames.length > 0) {
          // Add epic to each team group
          for (const teamName of teamNames) {
            if (!teamNameToEpics.has(teamName)) {
              teamNameToEpics.set(teamName, []);
            }
            teamNameToEpics.get(teamName)!.push(epic);
          }
        } else {
          // Track epics with no teams
          unmappedEpics.push(epic);
        }
      }

      // Add teams in sorted order
      const sortedTeamNames = Array.from(teamNameToEpics.keys()).sort((a, b) => a.localeCompare(b));

      for (const teamName of sortedTeamNames) {
        groups[teamName] = teamNameToEpics.get(teamName)!;
      }

      // Add unmapped group at the end
      if (unmappedEpics.length > 0) {
        groups['no-team'] = unmappedEpics;
      }

      return { groupedEpics: groups, orderedGroupKeys: Object.keys(groups) };
    }

    return { groupedEpics: {}, orderedGroupKeys: [] };
  }, [epics, groupBy]);

  // Pre-compute sorted epics for all groups
  const sortedGroupedEpics = useMemo(() => {
    const sorted: Record<string, StrategicEpic[]> = {};
    for (const groupId of orderedGroupKeys) {
      const epicsInGroup = groupedEpics[groupId];
      if (epicsInGroup && epicsInGroup.length > 0) {
        sorted[groupId] = sortStrategyEntities(
          epicsInGroup as any,
          tableSortBy as string,
          tableSortDirection,
        ) as StrategicEpic[];
      }
    }
    return sorted;
  }, [groupedEpics, orderedGroupKeys, tableSortBy, tableSortDirection]);

  if (epics.length === 0) {
    return <ContentPlaceholder />;
  }

  const handleGroupToggle = (groupId: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  const getGroupLabel = (groupId: string): string => {
    if (groupBy === TableGroupBy.Team) {
      if (groupId === 'no-team') {
        return `No ${i18n.t('common.team')}`;
      }
      // Team names are already stored as keys
      return groupId;
    }
    return groupId;
  };

  return (
    <HiddenScrollArea h="100%" type="auto">
      <Stack gap="md" p="md">
        {orderedGroupKeys.map((groupId) => {
          const epicsInGroup = groupedEpics[groupId];
          if (!epicsInGroup || epicsInGroup.length === 0) {
            return null;
          }

          const isExpanded = expandedGroups[groupId] ?? true;
          const groupLabel = getGroupLabel(groupId);
          const sortedEpicsInGroup = sortedGroupedEpics[groupId] || [];

          return (
            <GroupTable key={groupId}>
              <GroupHeader onClick={() => handleGroupToggle(groupId)}>
                <Icon
                  name={isExpanded ? 'expand_more' : 'chevron_right'}
                  size={20}
                  color={inkBase}
                  style={{ cursor: 'pointer' }}
                />
                <Text weight="medium">
                  {groupLabel} ({epicsInGroup.length})
                </Text>
              </GroupHeader>

              {isExpanded && (
                <div style={{ marginTop: 8 }}>
                  <Table<StrategicEpic>
                    columns={epicsTableColumns}
                    data={sortedEpicsInGroup}
                    getRowId={(epic) => epic.id}
                  />
                </div>
              )}
            </GroupTable>
          );
        })}
      </Stack>
    </HiddenScrollArea>
  );
};

const HiddenScrollArea = styled(ScrollArea)`
  div[data-orientation='vertical'] {
    display: none;
  }
`;

const GroupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background-color: ${skyLighter};
  border-radius: 8px;
  cursor: pointer;
  user-select: none;

  &:hover {
    background-color: ${skyLight};
  }
`;

const GroupTable = styled.div`
  margin-bottom: 20px;
`;
